/**
 * Shop email marketing (route /sites/:id/marketing/:tab?): overview + settings, sending domain, campaigns, automations,
 * subscribers, segments, sign-up forms, suppressions. API /api/user/sites/{id}/marketing/* (docs/SITES.md "Marketing").
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, AutoComplete, Button, Card, Checkbox, Col, Collapse, Form, Input, Popconfirm, Result, Row, Select, Space, Spin, Statistic, Switch, Table, Tabs, Tag, Typography, message,
} from 'antd';
import { CheckCircleFilled, CloseCircleFilled, MailOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import { CopyLine, money, sitePath, useSite } from './common';
import { mktError } from './MarketingCommon';
import MarketingCampaigns from './MarketingCampaigns';
import MarketingAutomations from './MarketingAutomations';
import { Segments, SignupForms, Subscribers, Suppressions } from './MarketingAudience';

const { Text } = Typography;

const Ok = ({ ok }) => (ok ? <CheckCircleFilled style={{ color: '#52c41a' }} /> : <CloseCircleFilled style={{ color: '#ff4d4f' }} />);

const SendingDomain = ({ data, onChange }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [domain, setDomain] = useState('');
    const [busy, setBusy] = useState(null);
    const s = data?.sending || {};
    const d = s.domain;
    const run = async (key, fn) => {
        setBusy(key);
        try { await fn(); onChange(); } catch (e) { mktError(e); } finally { setBusy(null); }
    };
    return (
        <Space direction="vertical" size={16} style={{ width: '100%', maxWidth: 900 }}>
            {s.stream ? (
                <Card size="small" title={t('Sending stream')} extra={<a href={userPath(`/tm/streams/${s.stream.id}`)}>{t('Manage')}</a>}>
                    <Space wrap>
                        <Text strong>{s.stream.name}</Text>
                        <Tag color={s.stream.status === 'active' ? 'green' : 'orange'}>{t(s.stream.status)}</Tag>
                        {s.stream.monthly_limit != null && <Text type="secondary">{t('This month')}: {s.stream.monthly_used ?? 0} / {s.stream.monthly_limit}</Text>}
                        {s.stream.trust_ok === false && <Tag color="red">{t('Sender level too low')}</Tag>}
                    </Space>
                    {(s.streams || []).length > 1 && (
                        <Select size="small" style={{ width: 260, marginTop: 8, display: 'block' }} value={s.stream.id}
                            onChange={(stream_id) => run('stream', () => api.post('/marketing/sending-domain/stream', { stream_id }))}
                            options={s.streams.map((x) => ({ value: x.id, label: `${x.name} (${t(x.status)})` }))} />
                    )}
                </Card>
            ) : (
                <Alert type="info" showIcon message={t('Email marketing sends through your own email sending (TM) marketing plan.')}
                    action={<Button size="small" type="primary" href={userPath('/tm/streams')}>{t('Open email sending')}</Button>} />
            )}
            <Card size="small" title={t('Sending domain')}>
                {!d ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Space.Compact style={{ width: '100%', maxWidth: 480 }}>
                            <AutoComplete style={{ flex: 1 }} value={domain} onChange={setDomain} placeholder="example.com"
                                options={(s.candidates || []).map((c) => ({ value: c.domain, label: <Space>{c.domain}{c.verified && <Tag color="green">{t('Verified')}</Tag>}</Space> }))} />
                            <Button type="primary" loading={busy === 'set'} disabled={!domain.trim()} onClick={() => run('set', () => api.post('/marketing/sending-domain', { domain: domain.trim().toLowerCase() }))}>{t('Use')}</Button>
                        </Space.Compact>
                        {s.domain_hint && <Text type="secondary" style={{ fontSize: 12 }}>{s.domain_hint}</Text>}
                    </Space>
                ) : (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Space wrap>
                            <Text strong style={{ fontSize: 16 }}>{d.domain}</Text>
                            {d.ready ? <Tag color="green">{t('Ready')}</Tag> : <Tag color="orange">{t('Not verified')}</Tag>}
                            {d.dns_hosted && <Tag color="blue">{t('DNS on Yeap')}</Tag>}
                            <Text type="secondary">{t('From')}: {d.from}</Text>
                            <Button size="small" icon={<ReloadOutlined />} loading={busy === 'verify'} onClick={() => run('verify', () => api.post('/marketing/sending-domain/verify'))}>{t('Verify')}</Button>
                            <Popconfirm title={t('Remove this sending domain?')} onConfirm={() => run('clear', () => api.del('/marketing/sending-domain'))}>
                                <Button size="small" danger type="text">{t('Remove')}</Button>
                            </Popconfirm>
                        </Space>
                        <Space size={16} wrap>
                            {Object.entries(d.checks || {}).map(([k, v]) => <span key={k}><Ok ok={v} /> {k.toUpperCase().replace('_', '-')}</span>)}
                        </Space>
                        {(d.records || []).length > 0 && (
                            <Table size="small" rowKey={(r) => `${r.key}-${r.type}-${r.host}`} pagination={false} dataSource={d.records} scroll={{ x: 760 }}
                                columns={[
                                    { title: '', dataIndex: 'ok', width: 32, render: (v) => <Ok ok={v} /> },
                                    { title: t('Type'), dataIndex: 'type', width: 70 },
                                    { title: t('Name'), dataIndex: 'host', width: 220, render: (v, r) => <CopyLine value={v || r.fqdn} /> },
                                    { title: t('Value'), dataIndex: 'value', render: (v) => <CopyLine value={v} /> },
                                    { title: '', dataIndex: 'required', width: 90, render: (v) => (v ? null : <Text type="secondary" style={{ fontSize: 12 }}>{t('Optional')}</Text>) },
                                ]} />
                        )}
                        {d.checked_at && <Text type="secondary" style={{ fontSize: 12 }}>{t('Checked')}: {d.checked_at}</Text>}
                    </Space>
                )}
            </Card>
            {s.blocked && !s.can_send && <Alert type="warning" showIcon message={s.blocked.error} />}
        </Space>
    );
};

const Overview = ({ data, onChange, goDomain }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const st = data?.settings || {};
    const o = data?.overview || {};
    useEffect(() => { form.setFieldsValue(st); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
    const save = async (patch) => {
        setSaving(true);
        try { await api.put('/marketing/settings', patch); message.success(t('Saved')); onChange(); } catch (e) { mktError(e, { goDomain }); } finally { setSaving(false); }
    };
    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {st.admin_paused && <Alert type="error" showIcon message={t('Email marketing was paused by Yeap')} description={st.admin_pause_reason} />}
            <Card size="small">
                <Space wrap>
                    <Switch checked={!!st.enabled} loading={saving} onChange={(enabled) => save({ enabled })} />
                    <Text strong>{st.enabled ? t('Email marketing is on') : t('Email marketing is off')}</Text>
                    {data?.sending && !data.sending.can_send && st.enabled && (
                        <Button size="small" type="link" onClick={goDomain}>{data.sending.blocked?.error_code === 'sending_domain_required' ? t('Connect a sending domain') : t('Check sending setup')}</Button>
                    )}
                </Space>
            </Card>
            <Card size="small" title={t('Last 30 days')}>
                <Row gutter={[16, 16]}>
                    <Col xs={12} md={6}><Statistic title={t('Subscribers')} value={o.subscribers || 0} suffix={o.new_subscribers ? <Text type="success" style={{ fontSize: 13 }}>+{o.new_subscribers}</Text> : null} /></Col>
                    <Col xs={12} md={6}><Statistic title={t('Emails sent')} value={o.sent || 0} /></Col>
                    <Col xs={12} md={6}><Statistic title={t('Opened')} value={o.opened || 0} /></Col>
                    <Col xs={12} md={6}><Statistic title={t('Clicked')} value={o.clicked || 0} /></Col>
                    <Col xs={12} md={6}><Statistic title={t('Orders')} value={o.orders || 0} /></Col>
                    <Col xs={12} md={6}><Statistic title={t('Revenue')} value={money(o.revenue || 0, o.currency)} /></Col>
                    <Col xs={12} md={6}><Statistic title={t('Unsubscribes')} value={o.unsubscribes || 0} /></Col>
                    <Col xs={12} md={6}><Statistic title={t('Bounced')} value={(o.bounced || 0) + (o.complained || 0)} /></Col>
                </Row>
            </Card>
            <Card size="small" title={t('Sender')}>
                <Form form={form} layout="vertical" style={{ maxWidth: 640 }} requiredMark={false}>
                    <Row gutter={12}>
                        <Col xs={24} sm={12}><Form.Item name="from_name" label={t('From name')}><Input maxLength={120} /></Form.Item></Col>
                        <Col xs={24} sm={12}><Form.Item name="from_local" label={t('From address')}><Input addonAfter={data?.sending?.domain ? `@${data.sending.domain.domain}` : '@…'} /></Form.Item></Col>
                        <Col xs={24} sm={12}><Form.Item name="reply_to" label={t('Reply-to')} rules={[{ type: 'email' }]}><Input /></Form.Item></Col>
                        <Col span={24}><Form.Item name="postal_address" label={t('Postal address (shown in every email)')} rules={[{ required: true }]}><Input.TextArea autoSize={{ minRows: 2 }} maxLength={500} /></Form.Item></Col>
                    </Row>
                    <Collapse ghost size="small" style={{ marginBottom: 12 }} items={[{
                        key: 'a', label: t('Advanced'),
                        children: (
                            <>
                                <Form.Item name="double_opt_in" valuePropName="checked" style={{ marginBottom: 4 }}><Checkbox>{t('Double opt-in (new subscribers confirm by email)')}</Checkbox></Form.Item>
                                <Form.Item name="txn_use_domain" valuePropName="checked"><Checkbox>{t('Send order emails from this domain too')}</Checkbox></Form.Item>
                            </>
                        ),
                    }]} />
                    <Button type="primary" loading={saving} onClick={async () => save(await form.validateFields())}>{t('Save')}</Button>
                </Form>
            </Card>
        </Space>
    );
};

const TABS = ['overview', 'domain', 'campaigns', 'automations', 'subscribers', 'segments', 'forms', 'suppressions'];

const Marketing = () => {
    const { t } = useTranslation();
    const { tab = 'overview' } = useParams();
    const navigate = useNavigate();
    const { siteId, api } = useSite();
    const [data, setData] = useState(null);
    const [soon, setSoon] = useState(false);
    const load = useCallback(async () => {
        try { setData(await api.get('/marketing', null, { skipGlobalErrorToast: true })); } catch (e) { if (e?.status === 404) setSoon(true); else mktError(e); setData((d) => d || {}); }
    }, [api]);
    useEffect(() => { load(); }, [load]);
    const go = (k) => navigate(sitePath(siteId, `marketing/${k}`));
    const goDomain = () => go('domain');
    if (soon) return <Card><Result icon={<MailOutlined />} title={t('Email marketing is coming soon')} /></Card>;
    if (!data) return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;
    const label = { overview: 'Overview', domain: 'Sending domain', campaigns: 'Campaigns', automations: 'Automations', subscribers: 'Subscribers', segments: 'Segments', forms: 'Sign-up forms', suppressions: 'Suppressed' };
    const notReady = data.sending && !data.sending.domain?.ready;
    const body = {
        overview: <Overview data={data} onChange={load} goDomain={goDomain} />,
        domain: <SendingDomain data={data} onChange={load} />,
        campaigns: <MarketingCampaigns goDomain={goDomain} />,
        automations: <MarketingAutomations goDomain={goDomain} />,
        subscribers: <Subscribers />,
        segments: <Segments />,
        forms: <SignupForms />,
        suppressions: <Suppressions />,
    };
    return (
        <Card size="small">
            <Tabs activeKey={TABS.includes(tab) ? tab : 'overview'} onChange={go} destroyInactiveTabPane
                items={TABS.map((k) => ({ key: k, label: <span>{t(label[k])}{k === 'domain' && notReady ? <Tag color="orange" style={{ marginLeft: 6 }}>!</Tag> : null}</span>, children: body[k] }))} />
        </Card>
    );
};

export default Marketing;
